import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Sparkles, X, View, Ruler, Satellite, Rocket } from 'lucide-react';

const VISIBLE_MS = 10000;
const HIDDEN_MS = 10000;
const INITIAL_DELAY_MS = 3000;
const DISMISS_KEY = 'acremitra_announcement_dismissed';

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1500382017468-9049fed747ef?w=900&q=80';

export const AnnouncementPopup = () => {
  // Suppress on share-link pages — customer arrived for a specific property,
  // not marketing. Also suppress on any /admin/* route.
  const onSharePage = typeof window !== 'undefined' && (
    window.location.pathname.startsWith('/property/') ||
    window.location.pathname.startsWith('/admin')
  );
  const [visible, setVisible] = useState(false);
  const [dismissed, setDismissed] = useState(
    () => onSharePage || (typeof window !== 'undefined' && sessionStorage.getItem(DISMISS_KEY) === '1')
  );

  useEffect(() => {
    if (dismissed) return;
    let cancelled = false;
    let visibleTimer;
    let hiddenTimer;

    const dialogOpen = () => !!document.querySelector('[role="dialog"][data-state="open"]');

    const showCycle = () => {
      if (cancelled) return;
      // Skip when a modal dialog is already occupying the screen — the Radix
      // focus-trap would otherwise swallow clicks on our close button.
      if (dialogOpen()) {
        hiddenTimer = setTimeout(showCycle, HIDDEN_MS);
        return;
      }
      setVisible(true);
      visibleTimer = setTimeout(() => {
        if (cancelled) return;
        setVisible(false);
        hiddenTimer = setTimeout(showCycle, HIDDEN_MS);
      }, VISIBLE_MS);
    };

    const startTimer = setTimeout(showCycle, INITIAL_DELAY_MS);
    return () => {
      cancelled = true;
      clearTimeout(startTimer);
      clearTimeout(visibleTimer);
      clearTimeout(hiddenTimer);
    };
  }, [dismissed]);

  const handleDismiss = () => {
    sessionStorage.setItem(DISMISS_KEY, '1');
    setDismissed(true);
    setVisible(false);
  };

  if (dismissed || !visible) return null;

  return createPortal(
    <div
      className="fixed inset-0 z-[900] flex items-center justify-center p-4 bg-black/40 backdrop-blur-sm animate-popupFade"
      onClick={handleDismiss}
      data-testid="announcement-popup"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="relative w-full max-w-[520px] bg-white rounded-2xl shadow-2xl overflow-hidden animate-popupScale"
      >
        {/* Dismiss */}
        <button
          onClick={handleDismiss}
          className="absolute top-3 right-3 w-8 h-8 rounded-full bg-black/40 hover:bg-black/60 text-white flex items-center justify-center transition-colors z-20 backdrop-blur-md"
          aria-label="Dismiss announcement"
          data-testid="announcement-dismiss"
        >
          <X size={16} />
        </button>

        {/* Hero image */}
        <div className="relative h-44 md:h-52 overflow-hidden">
          <img
            src={HERO_IMAGE}
            alt="AI-powered land discovery — aerial view"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-emerald-900/95 via-emerald-900/40 to-transparent"></div>
          <div className="absolute bottom-3 left-4 right-12">
            <div className="inline-flex items-center gap-1.5 px-2.5 py-1 bg-amber-400/95 rounded-full mb-2">
              <span className="text-[10px] font-bold tracking-[0.15em] uppercase text-emerald-900">
                Coming Soon · India First
              </span>
            </div>
            <h3 className="text-lg md:text-xl font-bold text-white leading-snug drop-shadow-lg">
              India's first <span className="text-amber-300">AI-powered</span> land discovery
            </h3>
          </div>
        </div>

        {/* Body */}
        <div className="px-5 py-5 bg-gradient-to-br from-white to-emerald-50/40">
          <ul className="space-y-3 text-[13px] text-slate-700">
            <li className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <View size={15} className="text-emerald-700" />
              </span>
              <span className="pt-1">
                <strong className="text-slate-900 block leading-tight">360° immersive land view</strong>
                <span className="text-slate-600 text-xs">Explore every plot from every angle — before you visit.</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Ruler size={15} className="text-emerald-700" />
              </span>
              <span className="pt-1">
                <strong className="text-slate-900 block leading-tight">AI land measurement on click</strong>
                <span className="text-slate-600 text-xs">Tap any point on the image — instant accurate dimensions.</span>
              </span>
            </li>
            <li className="flex items-start gap-3">
              <span className="w-8 h-8 rounded-lg bg-emerald-100 flex items-center justify-center flex-shrink-0">
                <Satellite size={15} className="text-emerald-700" />
              </span>
              <span className="pt-1">
                <strong className="text-slate-900 block leading-tight">Direct satellite view</strong>
                <span className="text-slate-600 text-xs">Verify location, surroundings & access in one click.</span>
              </span>
            </li>
          </ul>

          <div className="flex items-center gap-2 mt-5 pt-4 border-t border-slate-200 text-[12px] text-slate-500">
            <Rocket size={12} className="text-amber-600" />
            <span>Be the first to experience the future of land discovery.</span>
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default AnnouncementPopup;
